import { useLazyQuery, useQuery } from '@apollo/client';
import type { FlagQueryFilterForGrid } from '@generated/graphql';
import { usePageState } from '@utils/usePageState';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReconFlagsFiltersCardProps } from '../filters/reconFlagsFiltersCard.types';
import {
  FETCH_FLAG_TYPES,
  SEARCH_ACCOUNTS_FOR_RECON_FLAGS_GRID,
} from './flags.queries';
import {
  RECON_FLAGS_MIN_SEARCH_LENGTH,
  RECON_FLAGS_SEARCH_DEBOUNCE_MS,
} from './reconFlags.config';
import type { ReconFlagSeverity } from './reconFlags.types';
import {
  buildReconFlagsAccountIdsFilter,
  buildSearchAccountsForReconFlagsVariables,
  shouldSkipFetchFlagsForAccountSearch,
} from './reconFlagsAccountSearch';
import {
  hasReconFlagsActiveFilters,
  RECON_FLAGS_DEFAULT_FILTER_VALUES,
} from './reconFlagsFilterDefaults';
import {
  buildReconFlagsGridFilter,
  parseReconFlagsSeverityTab,
} from './reconFlagsGridVariables';

type UseReconFlagsFiltersInput = {
  orgId: string | undefined;
  setPage: (page: number) => void;
};

export const useReconFlagsFilters = ({
  orgId,
  setPage,
}: UseReconFlagsFiltersInput): {
  gridFilter: FlagQueryFilterForGrid | null;
  skipFlagsForAccountSearch: boolean;
  accountSearchInFlight: boolean;
  filtersProps: Omit<ReconFlagsFiltersCardProps, 'sys'>;
} => {
  const [flagState, setFlagState] = usePageState(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.flagState,
    'rfState',
  );
  const [flagType, setFlagType] = usePageState<string | undefined>(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.flagType,
    'rfType',
  );
  const [severityTabStored, setSeverityTabStored] = usePageState(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.severityTab,
    'rfSev',
  );
  const severityTab = useMemo(
    () => parseReconFlagsSeverityTab(severityTabStored),
    [severityTabStored],
  );
  const [categoryFilter, setCategoryFilter] = usePageState<string>(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.categoryFilter,
    'rfCat',
  );
  const [agingFilter, setAgingFilter] = usePageState<string>(
    RECON_FLAGS_DEFAULT_FILTER_VALUES.agingFilter,
    'rfAge',
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tmr = window.setTimeout(
      () => setDebouncedSearch(search),
      RECON_FLAGS_SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(tmr);
  }, [search]);

  const [runAccountSearch, accountSearch] = useLazyQuery(
    SEARCH_ACCOUNTS_FOR_RECON_FLAGS_GRID,
    { fetchPolicy: 'no-cache' },
  );
  const [accountsForSearchQuery, setAccountsForSearchQuery] = useState<
    string | null
  >(null);
  const [accountSearchPending, setAccountSearchPending] = useState(false);
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;
  const prevTrimmedDebouncedSearchRef = useRef(debouncedSearch.trim());
  const accountSearchRequestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (!orgId || trimmed.length < RECON_FLAGS_MIN_SEARCH_LENGTH) {
      setAccountsForSearchQuery(null);
      setAccountSearchPending(false);
      return;
    }
    const requestId = ++accountSearchRequestIdRef.current;
    setAccountsForSearchQuery(null);
    setAccountSearchPending(true);
    void runAccountSearch({
      variables: buildSearchAccountsForReconFlagsVariables(orgId, trimmed),
    })
      .then(() => {
        if (accountSearchRequestIdRef.current !== requestId) {
          return;
        }
        if (debouncedSearchRef.current.trim() === trimmed) {
          setAccountsForSearchQuery(trimmed);
        }
      })
      .finally(() => {
        if (accountSearchRequestIdRef.current === requestId) {
          setAccountSearchPending(false);
        }
      });
  }, [debouncedSearch, orgId, runAccountSearch]);

  const accountSearchInFlight = accountSearch.loading || accountSearchPending;

  const accountIdsFilter = useMemo(
    () =>
      buildReconFlagsAccountIdsFilter(debouncedSearch, {
        loading: accountSearchInFlight,
        accounts: accountSearch.data?.searchAccounts?.accounts,
        accountsForSearchQuery,
      }),
    [
      debouncedSearch,
      accountSearchInFlight,
      accountSearch.data?.searchAccounts?.accounts,
      accountsForSearchQuery,
    ],
  );

  useEffect(() => {
    const trimmed = debouncedSearch.trim();
    if (prevTrimmedDebouncedSearchRef.current === trimmed) {
      return;
    }
    prevTrimmedDebouncedSearchRef.current = trimmed;
    setPage(1);
  }, [debouncedSearch, setPage]);

  const gridFilter = useMemo(
    () =>
      buildReconFlagsGridFilter({
        orgId,
        flagState,
        flagType,
        severityTab,
        categoryFilter,
        agingFilter,
        accountIds: accountIdsFilter,
      }),
    [
      orgId,
      flagState,
      flagType,
      agingFilter,
      severityTab,
      categoryFilter,
      accountIdsFilter,
    ],
  );

  const skipFlagsForAccountSearch = shouldSkipFetchFlagsForAccountSearch(
    debouncedSearch,
    accountSearchInFlight,
    accountsForSearchQuery,
  );

  const { data: flagTypesData } = useQuery(FETCH_FLAG_TYPES);
  const flagTypes = useMemo(() => {
    const names =
      flagTypesData?.__type?.enumValues?.map(
        (enumValue: { name: string }) => enumValue.name,
      ) ?? [];
    return names as string[];
  }, [flagTypesData]);

  const onSeverityTabChange = useCallback(
    (tab: 'ALL' | ReconFlagSeverity) => {
      setSeverityTabStored(tab);
      setPage(1);
    },
    [setPage, setSeverityTabStored],
  );

  const onFlagStateChange = useCallback(
    (value: string) => {
      setFlagState(value);
      setPage(1);
    },
    [setFlagState, setPage],
  );

  const onFlagTypeChange = useCallback(
    (value: string | undefined) => {
      setFlagType(value);
      setPage(1);
    },
    [setFlagType, setPage],
  );

  const onCategoryFilterChange = useCallback(
    (value: string) => {
      setCategoryFilter(value);
      setPage(1);
    },
    [setCategoryFilter, setPage],
  );

  const onAgingFilterChange = useCallback(
    (value: string) => {
      setAgingFilter(value);
      setPage(1);
    },
    [setAgingFilter, setPage],
  );

  const hasActiveFilters = useMemo(
    () =>
      hasReconFlagsActiveFilters({
        severityTab,
        flagState,
        flagType,
        categoryFilter,
        agingFilter,
        search,
      }),
    [severityTab, flagState, flagType, categoryFilter, agingFilter, search],
  );

  const onClearFilters = useCallback(() => {
    setFlagState(RECON_FLAGS_DEFAULT_FILTER_VALUES.flagState);
    setFlagType(RECON_FLAGS_DEFAULT_FILTER_VALUES.flagType);
    setSeverityTabStored(RECON_FLAGS_DEFAULT_FILTER_VALUES.severityTab);
    setCategoryFilter(RECON_FLAGS_DEFAULT_FILTER_VALUES.categoryFilter);
    setAgingFilter(RECON_FLAGS_DEFAULT_FILTER_VALUES.agingFilter);
    setSearch(RECON_FLAGS_DEFAULT_FILTER_VALUES.search);
    setDebouncedSearch(RECON_FLAGS_DEFAULT_FILTER_VALUES.search);
    setAccountsForSearchQuery(null);
    setAccountSearchPending(false);
    accountSearchRequestIdRef.current += 1;
    setPage(1);
  }, [
    setAgingFilter,
    setCategoryFilter,
    setFlagState,
    setFlagType,
    setPage,
    setSeverityTabStored,
  ]);

  const filtersProps = useMemo(
    (): Omit<ReconFlagsFiltersCardProps, 'sys'> => ({
      severityTab,
      onSeverityTabChange,
      flagState,
      onFlagStateChange,
      flagType,
      onFlagTypeChange,
      flagTypes,
      categoryFilter,
      onCategoryFilterChange,
      agingFilter,
      onAgingFilterChange,
      search,
      onSearchChange: setSearch,
      hasActiveFilters,
      onClearFilters,
    }),
    [
      severityTab,
      onSeverityTabChange,
      flagState,
      onFlagStateChange,
      flagType,
      onFlagTypeChange,
      flagTypes,
      categoryFilter,
      onCategoryFilterChange,
      agingFilter,
      onAgingFilterChange,
      search,
      hasActiveFilters,
      onClearFilters,
    ],
  );

  return {
    gridFilter,
    skipFlagsForAccountSearch,
    accountSearchInFlight,
    filtersProps,
  };
};
